<template>
  <div>
    <HKLModal
      ref="modal"
      class="temp-close-reopen-modal"
      okBtnAction="saveAndNotify"
      size="medium"
      :title="title"
      @onCloseClick="cleanModalState"
    >
      <div class="modal-content check-valid">
        <div class="date-group row">
          <template v-if="record.type === MOVE_TYPE.TEMP_ClOSE">
            <span class="required-flag date-group__label">{{ $t('handover.moveInOut.duration') }}</span>
            <HKLDateRange
              v-model="record.duration"
              :required="true"
              :displayRange="[{ to: record.duration[1] }, { to: null }]"
              :placeholders="[$t('projectViewAll.startFrom'), $t('projectViewAll.until')]"
            />
          </template>
          <template v-if="record.type === MOVE_TYPE.REOPEN">
            <span class="required-flag date-group__label">{{ $t('handover.moveInOut.reopenDate') }}</span>
            <DatePicker
              :required="true"
              :calendarButton="true"
              calendarButtonIcon="fa fa-calendar"
              :value="formatDate(record.date)"
              @selected="val => updateRecord('date', val)"
            />
          </template>
        </div>

        <TextField
          class="premises-textfield"
          :required="true"
          :label="$t('handover.moveInOut.header.premises')"
          :placeholder="$t('handover.moveInOut.pleaseInputFloorPremises')"
          :value="record.premises"
          @updateValue="val => updateRecord('premises', val)"
        />

        <TextField
          class="remark-textfield"
          :label="$t('handover.moveInOut.header.remarks')"
          :value="record.remark"
          @updateValue="val => updateRecord('remark', val)"
        />
      </div>
    </HKLModal>
    <HKLModal
      ref="savingConfirmModalRef"
      :title="$t('handover.moveInOut.message.beforeSaving')"
      @onOkClick="save"
    />
  </div>
</template>

<script>
import lodash from 'lodash'
import {
  computed,
  reactive,
  unref,
  onMounted,
} from 'vue-demi'
import { useStoreGetters } from '@/store'
import HKLModal from '@/controls/Modal/HKLModal'
import TextField from '@/controls/TextField'
import HKLDateRange from '@/controls/Date/HKLDateRange'
import DatePicker from '@/controls/DatePicker'
import { useRefModal } from '@/controls/Modal/modal.hook'
import { MOVE_TYPE } from './const'
import i18n from '@/utils/i18n'
import dateUtil from '@/utils/dateUtil'
import { ccdRouteNames } from '@/utils/consts'
import { createMoveInOutAndNotify, updateMoveInOutAndNotify } from '@/api/tenancies/request'
import { getPublicLink } from '@/api/postman/util'
import { fusionInputCheckUtil } from '@/plugin/FusionInputCheck'
import { utils as formUtils } from '@/plugin/InputChange'

export const EVENT_TEMP_REOPEN_TYPE = {
  ON_AFTER_SAVE: 'onAfterSave',
}

export default ({
  components: {
    HKLModal,
    TextField,
    HKLDateRange,
    DatePicker,
  },
  setup (props, { emit }) {
    const { currentTenancy } = useStoreGetters('ccd', ['currentTenancy'])
    const modals = useRefModal()
    const initRecordState = {
      id: '',
      type: '',
      date: '',
      businessDate: '',
      duration: [],
      premises: '',
      remark: '',
    }
    const record = reactive({ ...initRecordState })
    const cleanModalState = () => {
      Object.keys(initRecordState).forEach(key => {
        record[key] = initRecordState[key]
      })
      formUtils.resetDirty()
    }
    const openNewRecord = (type) => {
      record.type = type
      modals.open()
    }
    const formatDate = (item) => {
      return dateUtil.formatDate(item, false)
    }
    const openEditRecord = (item) => {
      Object.keys(initRecordState).forEach(key => {
        record[key] = item[key]
      })
      if (record.type === MOVE_TYPE.TEMP_ClOSE) {
        record.duration = [new Date(record.date), new Date(record.businessDate)]
      }
      modals.open()
    }

    const title = computed(() => {
      if (record.type === MOVE_TYPE.TEMP_ClOSE) {
        return i18n.t('handover.moveInOut.addTempCloseRecord')
      }
      return i18n.t('handover.moveInOut.addReopenRecord')
    })

    const updateRecord = (key, value) => {
      record[key] = value
    }

    const save = async () => {
      if (record.type === MOVE_TYPE.TEMP_ClOSE) {
        record.date = record.duration[0]
        record.businessDate = record.duration[1]
      }

      const request = lodash.omit(record, 'duration')
      request.metaData = {
        date: dateUtil.formatDate(record.date),
        businessDate: dateUtil.formatDate(record.businessDate),
        remarks: record.remark || i18n.t('emptyValue'),
        premises: record.premises,
        tenancyId: unref(currentTenancy).id,
        tradeName: lodash.get(unref(currentTenancy), 'tradeName'),
        companyName: lodash.get(unref(currentTenancy), 'company.name'),
        building: lodash.get(unref(currentTenancy), 'building'),
        buildingFullName: lodash.get(unref(currentTenancy), 'building.fullName'),
        subsidiary: i18n.t('system.title'),
        type: lodash.get(unref(currentTenancy), 'type'),
        linkToAccess: getPublicLink(ccdRouteNames.ccdTenancyView, unref(currentTenancy).id),
      }
      if (request.id) {
        await updateMoveInOutAndNotify(unref(currentTenancy).id, request)
      } else {
        await createMoveInOutAndNotify(unref(currentTenancy).id, request)
      }
      modals.close()
      emit(EVENT_TEMP_REOPEN_TYPE.ON_AFTER_SAVE)
      cleanModalState()
    }

    const validateFields = () => {
      fusionInputCheckUtil.validateAllFields({allowEmpty: 'none'}, '.check-valid')
      return fusionInputCheckUtil.isAllFieldsValid('.check-valid')
    }

    const savingConfirmModals = useRefModal()
    const openSaveConfirm = () => {
      if (!validateFields()) return
      savingConfirmModals.open()
    }

    onMounted(() => {
      modals.modal.value.onClick = openSaveConfirm
    })

    return {
      modal: modals.modal,
      openNewRecord,
      openEditRecord,
      title,
      record,
      MOVE_TYPE,
      formatDate,
      savingConfirmModalRef: savingConfirmModals.modal,
      save,
      validateFields,
      openSaveConfirm,
      updateRecord,
      cleanModalState,
    }
  },
})
</script>
<style lang="scss" scoped>

.temp-close-reopen-modal {
  ::v-deep .ant-modal-title {
    font-size: 16px;
  }
  ::v-deep .ant-modal-content .ant-modal-body {
    margin-left: 12px;
  }
  .modal-content {
    .date-group, .premises, .remark {
      margin-top: 12px;
      &__label {
        margin-right: 12px;
        font-weight: 700;
      }
    }
    .date-group {
      display: flex;
      align-items: center;
    }
  }
}

</style>
